import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import SearchBar from "@/components/feature/SearchBar";
import ContentListItem from "@/components/feature/ContentListItem";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import FilterSidebar, { type FilterSection } from "@/components/feature/FilterSidebar";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import type { ContentType } from "@/mocks/content";
import {
  countByType,
  getRelatedSearches,
  searchContent,
  type SortKey,
} from "@/utils/search";

const PAGE_SIZE = 5;

export default function Search() {
  const [params, setParams] = useSearchParams();
  const [showFilter, setShowFilter] = useState(false);

  const q = params.get("q") || "";
  const type = (params.get("type") as ContentType | "all") || "all";
  const sort = (params.get("sort") as SortKey) || "relevance";
  const page = Number(params.get("page") || "1") || 1;

  const result = searchContent({ keyword: q, type, sort, page, pageSize: PAGE_SIZE });
  const relatedSearches = getRelatedSearches(q, 8);

  useDocumentMeta({
    title: q ? `${q} - 综合搜索结果 - 百度一下` : "综合搜索 - 百度一下",
    description: q
      ? `在百度一下搜索“${q}”，聚合新闻、视频、百科等多领域内容，快速找到相关信息。`
      : "在百度一下进行综合搜索，聚合新闻、视频、百科等多领域内容。",
    keywords: q ? `${q},百度一下,综合搜索,信息检索` : "百度一下,综合搜索,信息检索",
  });

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (!value || value === "all" || (key === "sort" && value === "relevance")) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    next.delete("page");
    setParams(next);
  };

  const goPage = (p: number) => {
    const next = new URLSearchParams(params);
    if (p <= 1) next.delete("page");
    else next.set("page", String(p));
    setParams(next);
  };

  const typeOptions = [
    { label: "全部", value: "all", count: countByType(q, "all") },
    { label: "新闻", value: "news", count: countByType(q, "news") },
    { label: "视频", value: "video", count: countByType(q, "video") },
    { label: "百科", value: "encyclopedia", count: countByType(q, "encyclopedia") },
  ];

  const sections: FilterSection[] = [
    { title: "内容类型", value: type, options: typeOptions, onChange: (v) => updateParam("type", v) },
    {
      title: "排序方式",
      value: sort,
      options: [
        { label: "按相关度", value: "relevance" },
        { label: "按时间", value: "time" },
      ],
      onChange: (v) => updateParam("sort", v),
    },
  ];

  const resetAll = () => {
    setParams(new URLSearchParams(q ? { q } : {}));
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        {/* Search header */}
        <section className="border-b border-background-200 bg-background-100">
          <div className="w-full px-4 py-8 md:px-6 md:py-10">
            <div className="mx-auto max-w-6xl">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <SearchBar defaultValue={q} />
                <p className="text-sm text-foreground-600">
                  找到约 <span className="font-bold text-primary-600">{result.total}</span> 条结果
                </p>
              </div>
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-6xl px-4 py-8 md:px-6 md:py-10">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[240px_1fr]">
            {/* Sidebar filter */}
            <aside>
              <div className="mb-3 flex items-center justify-between lg:hidden">
                <button
                  type="button"
                  onClick={() => setShowFilter((v) => !v)}
                  className="flex items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-700"
                >
                  <i className="ri-filter-3-line" />
                  筛选
                  <i className={`ri-arrow-down-s-line transition-transform ${showFilter ? "rotate-180" : ""}`} />
                </button>
                <button
                  type="button"
                  onClick={resetAll}
                  className="text-sm text-primary-600"
                >
                  重置
                </button>
              </div>

              <div className="hidden lg:block">
                <FilterSidebar sections={sections} />
                <button
                  type="button"
                  onClick={resetAll}
                  className="mt-3 w-full rounded-md border border-background-300 px-3 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
                >
                  重置筛选
                </button>
              </div>

              {showFilter && (
                <div className="lg:hidden">
                  <FilterSidebar sections={sections} />
                </div>
              )}
            </aside>

            {/* Results */}
            <div className="min-w-0">
              <div className="mb-4 flex flex-wrap items-center gap-2 text-sm text-foreground-600">
                <span>搜索关键词：</span>
                <span className="font-medium text-foreground-950">“{q || "全部内容"}”</span>
                {type !== "all" && (
                  <span className="rounded bg-primary-100 px-2 py-0.5 text-xs text-primary-700">
                    {typeOptions.find((t) => t.value === type)?.label}
                  </span>
                )}
                {sort === "time" && (
                  <span className="rounded bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900">
                    按时间排序
                  </span>
                )}
              </div>

              {result.items.length > 0 ? (
                <>
                  <div className="flex flex-col gap-4">
                    {result.items.map((item) => (
                      <ContentListItem key={item.id} item={item} />
                    ))}
                  </div>
                  <Pagination current={result.page} totalPages={result.totalPages} onChange={goPage} />
                </>
              ) : (
                <EmptyState
                  title="没有找到相关结果"
                  description="换个关键词试试，或调整筛选条件重新搜索。当前为演示数据，实际搜索效果待接入后端支持。"
                  actions={
                    <>
                      <button
                        type="button"
                        onClick={resetAll}
                        className="rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600"
                      >
                        清除筛选条件
                      </button>
                      <Link
                        to="/"
                        className="rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 whitespace-nowrap transition-colors hover:border-primary-400 hover:text-primary-600"
                      >
                        返回首页
                      </Link>
                    </>
                  }
                />
              )}

              {/* Related searches */}
              <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5">
                <h3 className="flex items-center gap-1.5 font-heading text-sm font-bold text-foreground-950">
                  <i className="ri-link text-primary-500" />
                  相关搜索推荐
                </h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  {relatedSearches.map((kw) => (
                    <Link
                      key={kw}
                      to={`/search?q=${encodeURIComponent(kw)}`}
                      className="rounded-full border border-background-300 bg-background-50 px-3 py-1.5 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
                    >
                      {kw}
                    </Link>
                  ))}
                </div>
              </div>

              <p className="mt-4 text-xs text-foreground-400">
                演示范围，搜索与筛选基于前端示例数据，待接入后端支持。
              </p>
            </div>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}